import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterOutlet } from '@angular/router';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { LogoComponent } from '../../../../../shared/components/logo.component';
import { IconMenuItemsComponent } from '../../../../../shared/components/icon-menu-items.component';
import { UserPreferenceStore } from '../../../../services/user-preference/user-preference.store';
import { LayoutService } from 'app/shared/services/layout.service';
import { PageTitleComponent } from 'app/shared/components/page-title.component';
import { NavigationService } from '../../../../navigation/navigation.service';
import { ImpersonationBannerComponent } from 'app/core/layout/common/impersonation-banner/impersonation-banner.component';
import { RouteMapSidebarComponent } from '../../../common/route-map-sidebar/route-map-sidebar.component';
import { PoweredByComponent } from 'app/core/layout/common/powered-by/powered-by.component';
import { SystemSettingsStore } from 'app/core/services/system-settings.store';
import { getPoweredByLogoHeight } from 'app/core/services/theme.model';
import { publishStickyHeaderOffset } from 'app/core/layout/sticky-header-offset';

@Component({
  selector: 'classy-layout',
  templateUrl: './classy.component.html',
  styles: `
    fuse-vertical-navigation-basic-item:has(.menu-item--return) {
      position: fixed;
      bottom: 0;
      width: 280px;
      z-index: 10;
    }

    .menu-item--return {
      position: relative;
    }

    .fuse-vertical-navigation-content {
      padding-bottom: 48px;
    }
  `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FuseLoadingBarComponent,
    FuseVerticalNavigationComponent,
    MatIconModule,
    MatButtonModule,
    RouterOutlet,
    MatFormFieldModule,
    MatOptionModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatBadgeModule,
    MatSnackBarModule,
    FormsModule,
    LogoComponent,
    IconMenuItemsComponent,
    PageTitleComponent,
    ImpersonationBannerComponent,
    PoweredByComponent,
  ],
})
export class ClassyLayoutComponent implements OnInit {
  layoutService = inject(LayoutService);
  navigationService = inject(NavigationService);
  userPreferenceStore = inject(UserPreferenceStore);
  systemSettingsStore = inject(SystemSettingsStore);

  isSidebarFullScreen: boolean;
  navigationIsOpened = true;
  menuOpened = computed(() => {
    return this.userPreferenceStore.userPreference()?.ui?.mainMenu === 'open';
  });
  /** Footer supplier-logo height, admin-configurable per tenant (#21262 follow-up). Clamped/defaulted
   *  by `getPoweredByLogoHeight` — `dd-powered-by` itself stays presentational (input only). */
  poweredByLogoHeight = computed(() => getPoweredByLogoHeight(this.systemSettingsStore.branding()));

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  /**
   * Constructor
   */
  constructor(private _fuseNavigationService: FuseNavigationService) {
    // The bar is pinned (`sticky top-0` in the template): tell the consumers how tall it is.
    publishStickyHeaderOffset();

    // Create effect for screen size changes
    effect(() => {
      const isSmall = this.layoutService.isScreenSmall();

      if (isSmall) {
        // Force close navigation when switching to mobile
        const name = this.layoutService.isAdminPage() ? 'adminNavigation' : 'mainNavigation';
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation && navigation.opened) {
          this.navigationIsOpened = false;
          this.saveNavigationStatus(false);
        }
      }
    });
  }

  ngOnInit(): void {
    const name = this.layoutService.isAdminPage() ? 'adminNavigation' : 'mainNavigation';
    const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);
    if (navigation) {
      navigation.openedChanged.pipe(debounceTime(300), takeUntil(this._unsubscribeAll)).subscribe(() => {
        this.navigationIsOpened = navigation.opened;
        this.saveNavigationStatus(this.navigationIsOpened);
      });
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  toggleNavigation() {
    const name = this.layoutService.isAdminPage() ? 'adminNavigation' : 'mainNavigation';
    const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);
    if (navigation) {
      navigation.toggle();
      this.navigationIsOpened = navigation.opened;
      if (!this.layoutService.isScreenSmall()) {
        this.isSidebarFullScreen = !navigation.opened;
      } else {
        this.isSidebarFullScreen = true;
      }
    }
  }

  saveNavigationStatus(isOpen: boolean) {
    const currentState = this.userPreferenceStore.userPreference()?.ui?.mainMenu;
    const newState = isOpen ? 'open' : 'closed';

    if (currentState !== newState) {
      requestAnimationFrame(() => {
        this.userPreferenceStore.editUI({ mainMenu: newState });

        // Cleanup overlays after status change in mobile view
        if (!isOpen && this.layoutService.isScreenSmall()) {
          setTimeout(() => {
            this.layoutService.cleanupStuckOverlays();
          }, 350);
        }
      });
    }
  }
}
